import { useApiMutation, usePageData } from "../utils/api";
import { useParams } from "react-router-dom";
import { ProjectHeader } from "./project_header";
import { Offcanvas } from "react-bootstrap";
import { makeModal } from "../utils/make_modal";
import { ExpandableText } from "../utils/expandable_text";
import CreatableSelect from "react-select/creatable";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { capitalize } from "lodash";
import { Edit, Trash } from "lucide-react";
import styled from "styled-components";

const TAGS = ["Dev", "Definition", "Project in Progress", "Product"].map((tag) => ({
  label: capitalize(tag),
  value: tag,
}));

const useNotesForm = () =>
  makeModal(({ refetch, close, object }) => {
    const { post, put, saving, destroy } = useApiMutation();
    const { projectId } = useParams();
    const { register, handleSubmit, reset, control } = useForm({
      defaultValues: object?.id
        ? { title: object.title, content: object.content, tags: object.tags }
        : { title: "", content: "", tags: [] },
    });

    const onSubmit = (values) => {
      if (object?.id) {
        put(object._path, { note: values }).success(() => {
          refetch();
          close();
          toast.success("Note updated");
        });
      } else {
        post(`/projects/${projectId}/notes`, { note: values }).success(({ data }) => {
          reset();
          close();
          refetch();
          toast.success("Note created");
        });
      }
    };

    const destroyItem = (id) => {
      destroy(`/projects/${projectId}/notes/${id}`).success(({ data }) => {
        refetch();
        close();
        toast.success("Note deleted");
      });
    };

    return (
      <Offcanvas show={!!object} onHide={close} placement="end" className="w-700px">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{object?.id ? "Edit Item" : "Add New Item"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-3">
              <label className="form-label fw-600 fs-14">Title</label>
              <input
                type="text"
                className="form-control"
                required
                {...register("title")}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-600 fs-14">Content</label>
              <textarea
                className="form-control"
                required
                rows={8}
                {...register("content")}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fw-600 fs-14">Tags</label>
              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <CreatableSelect
                    isClearable
                    isMulti
                    value={(field.value || []).map((tag) => ({ label: tag, value: tag }))}
                    onChange={(a) => field.onChange(a.map((v) => v.value))}
                    options={TAGS}
                  />
                )}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <div
                role="button"
                className="text-danger"
                onClick={() => confirm("Delete this item?") && destroyItem(object.id)}
              >
                <Trash size={14} />
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={close}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </Offcanvas.Body>
      </Offcanvas>
    );
  });

const StyledTable = styled.table`
  &.table {
    tr:hover td {
      background-color: #fafafa;
    }
  }
`;

export const Notes = () => {
  const { data, unavailable, refetch } = usePageData();
  const NotesModal = useNotesForm();

  if (unavailable) {
    return unavailable;
  }

  return (
    <div>
      <ProjectHeader project={data.project} refetch={refetch} />

      <StyledTable className="table table-sm fs-14">
        <thead>
          <tr>
            <th className="w-25">Title</th>
            <th>Content</th>
            <th className="w-100px" />
          </tr>
        </thead>
        <tbody>
          {data?.notes?.map((item) => (
            <tr key={item.id}>
              <td>
                {item.title}
                <div className="fs-12 text-muted mt-1">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="badge text-bg-light text-light-emphasis fs-8 mh-1"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </td>
              <td className="pre-wrap">
                <ExpandableText>{item.content}</ExpandableText>
              </td>
              <td className="text-end">
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    className="btn btn-text blue p-0"
                    onClick={() => NotesModal.set(item)}
                  >
                    <Edit size={12} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </StyledTable>

      <div className="mt-5 text-end">
        <button className="btn btn-primary btn-sm" onClick={() => NotesModal.set({})}>
          + Add Item
        </button>
      </div>

      <NotesModal refetch={refetch} />
    </div>
  );
};
